import React, { useEffect } from "react";
import { Controller, useForm } from "react-hook-form";
import { intl } from "LocaleWrapper";
import { useMutation } from "@apollo/client";
import { zodResolver } from "@hookform/resolvers/zod";

import { FormGroup, Select } from "@cofano/cck";

import { FormDialog } from "components/dialogs/Dialogs";
import { FormErrorMessage } from "components/form/FormErrorMessage";

import { LocationSelectOption } from "view/locations/components/selects/LocationSelect";
import { updateBerthingLocationSchema, UpdateBerthingLocationSchemaInput, UpdateBerthingLocationSchemaOutput } from "view/connectingVisits/utils/connectingvisit.schema";

import { EditBerthingLocationDocument } from "__generated__/view/connectingVisits/graphql/connectingVisit.mutations.generated";

const UpdateBerthingLocationDialog = ({ open, onClose, berthing, locations, refetch }: {
	open: boolean,
	onClose: () => void,
	berthing: {
		id: number;
		location: LocationSelectOption | null,
	}
	locations: LocationSelectOption[],
	refetch: () => void,
}) => {

	const { control, handleSubmit, reset } = useForm<UpdateBerthingLocationSchemaInput, unknown, UpdateBerthingLocationSchemaOutput>({
		resolver: zodResolver(updateBerthingLocationSchema),
		defaultValues: {
			location: null,
		},
	});

	const handleOnClose = () => {
		onClose();
		reset({
			location: null,
		});
	};

	const [ editBerthingLocation, { loading } ] = useMutation(EditBerthingLocationDocument);

	const save = (data: UpdateBerthingLocationSchemaOutput) => {
		editBerthingLocation({
			variables: {
				id: berthing.id,
				locationId: data.location.id,
			},
		}).then(() => {
			refetch();
			handleOnClose();
		});
	};

	useEffect(() => {
		if (open) {
			reset({
				location: berthing.location,
			});
		}
	}, [ open ]);

	return (
		<FormDialog
			open={open}
			onClose={handleOnClose}
			title={intl.formatMessage({ id: "CONNECTING_VISIT_BERTH_LOCATION" })}
			form='updateBerthingLocationForm'
			loading={loading}>
			<form onSubmit={(event) => void handleSubmit(save)(event)} id="updateBerthingLocationForm">
				<FormGroup label={intl.formatMessage({ id: "LOCATION" })} overflow="hidden" required>
					<Controller
						name="location"
						control={control}
						render={({ field, formState }) => (
							<React.Fragment>
								<Select
									{ ...field }
									fetchOptions={locations}
									getOptionLabel={option => option?.description || ""} />
								<FormErrorMessage name={field.name} errors={formState.errors} />
							</React.Fragment>
						)} />
				</FormGroup>
			</form>
		</FormDialog>
	);
};

export default UpdateBerthingLocationDialog;
